import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { MarkovTransition } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import {
    PRM_MARKOV_MODEL,
    StoreType,
    MarkovTransitionList,
    MarkovTransitionCalculated,

    LoadAllMarkovTransitions,
    DeleteMarkovTransition,
    SortMarkovTransitions,
    MarkovExpressionCalc,

    setMarkovTransitionList,
    setMarkovTransitionIds,
    setMarkovTransitionSort,
    setMarkovTransitionCalculatedExpressions,
    addModal,
} from "client/store"

import {
    Collapse,
    Container,
    Loader,
    Button,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import EditTransition from "client/pages/markov/transition/edit"
import Columns from "./columns"


const List = () => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)

    const transitionList = useSelector((state: StoreType) => state.markovTransition.transitionList)
    const transitionIds = useSelector((state: StoreType) => state.markovTransition.transitionIds)
    const sortColumn = useSelector((state: StoreType) => state.markovTransition.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovTransition.sortOrder)
    const reloadList = useSelector((state: StoreType) => state.markovTransition.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [show, setShow] = useState(false)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllMarkovTransitions(model && model.id).then((res) => {
            dispatch(setMarkovTransitionList(res))
            setLoadState(IDLE)
        })
    }, [model, reloadList])

    useEffect(() => {
        if (
            !transitionList || !Object.values(transitionList).length
            || !variableList || !Object.values(variableList).length
        ) return
        const calculated: MarkovTransitionCalculated = {}
        Object.values(transitionList).forEach((transition) => {
            if (!transition.expression.length) return
            const calcResult = MarkovExpressionCalc(null, transition.expression, variableList, lang)
            if (calcResult.error) {
                calculated[transition.id] = calcResult.error
            } else {
                calculated[transition.id] = calcResult.value
            }
        })
        dispatch(setMarkovTransitionCalculatedExpressions(calculated))
    }, [transitionList, variableList])

    useEffect(() => {
        const sortedIds = SortMarkovTransitions(Object.values(transitionList), sortColumn, sortOrder)
        dispatch(setMarkovTransitionIds(sortedIds))
    }, [transitionList, sortColumn, sortOrder])

    const onSort: SortRows = (column: keyof MarkovTransition, order) => {
        dispatch(setMarkovTransitionSort(column, order))
    }

    const onDelete: DeleteRow = (transition: MarkovTransition, callback) => {
        DeleteMarkovTransition(transition.id, model.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const transitionListCopy: MarkovTransitionList = transitionList ? deepCopy(transitionList) : {}
                delete transitionListCopy[transition.id]
                dispatch(setMarkovTransitionList(transitionListCopy))
                dispatch(setMarkovTransitionIds(transitionIds.filter((id) => id !== transition.id)))
            })
        })
    }

    const columns = Columns()
    const editModelAccess = hasAccess(PRM_MARKOV_MODEL, EDIT)

    return (
        <Collapse
            show={show}
            onCollapse={() => setShow(!show)}
            title={lang.transitions}
        >
            <Container>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <Table
                    columns={columns}
                    dataList={transitionList}
                    dataIds={transitionIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    scrollToTopOnPagination={false}
                    onSort={onSort}
                    onDelete={onDelete}
                    bottomContent={editModelAccess && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                key: "addTransition",
                                content: <EditTransition transitionId={0} />,
                            }))}
                        >
                            {lang.add}
                        </Button>
                    )}
                />
            </Container>
        </Collapse>
    )
}
export default List
